/**
 * Board-packet assembly helpers for Exterior Improvement Applications (#363).
 *
 * SBS compiles each application into ONE packet for the Board/ARC: the filled
 * form first, then the supporting documents in the order the request type's
 * checklist specifies (plot map, current photos, product pics, …). Attachments
 * carry the checklist slot they satisfy in `itemKey`; anything untagged (staff
 * uploads, email attachments) sorts after the tagged pages in upload order.
 */

import type { Attachment } from '@/types/models';

export interface ChecklistItemRef {
	key: string;
	label: string;
}

const isImageMime = (m?: string | null): boolean => !!m && m.startsWith('image/');
const isPdfMime = (m?: string | null): boolean => m === 'application/pdf';

/**
 * Order attachments for the printed packet: checklist-tagged files first, in
 * checklist order (stable by createdAt within a slot), then untagged files by
 * createdAt.
 */
export function orderAttachmentsByChecklist<T extends Pick<Attachment, 'itemKey' | 'createdAt'>>(
	attachments: T[],
	checklist: ChecklistItemRef[],
): T[] {
	const indexByKey = new Map(checklist.map((item, index) => [item.key, index]));
	const createdMs = (a: T): number => {
		const t = a.createdAt ? new Date(a.createdAt).getTime() : 0;
		return Number.isNaN(t) ? 0 : t;
	};
	return [...attachments].sort((a, b) => {
		const ai = a.itemKey != null ? (indexByKey.get(a.itemKey) ?? Number.MAX_SAFE_INTEGER - 1) : Number.MAX_SAFE_INTEGER;
		const bi = b.itemKey != null ? (indexByKey.get(b.itemKey) ?? Number.MAX_SAFE_INTEGER - 1) : Number.MAX_SAFE_INTEGER;
		if (ai !== bi) return ai - bi;
		return createdMs(a) - createdMs(b);
	});
}

/** Split ordered attachments into printable image pages and a PDF manifest. */
export function splitPacketAttachments<T extends Pick<Attachment, 'mimeType'>>(
	ordered: T[],
): { images: T[]; pdfs: T[]; other: T[] } {
	const images: T[] = [];
	const pdfs: T[] = [];
	const other: T[] = [];
	for (const att of ordered) {
		if (isImageMime(att.mimeType)) images.push(att);
		else if (isPdfMime(att.mimeType)) pdfs.push(att);
		else other.push(att);
	}
	return { images, pdfs, other };
}

const PACKET_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']);

/**
 * THE rule for "is this file printed as a packet PAGE?" — by MIME **or** extension, because
 * two upload paths store photos as `application/octet-stream` (see `assemblePacket`).
 *
 * One definition, shared by the print page (what the packet SHOWS) and the board-approval
 * letter (what it ATTACHES beside the packet): everything this says no to is attached as its
 * own file, so the two can never disagree about which files the board actually receives.
 */
export function isPacketPage(file: { mimeType?: string | null; fileType?: string | null }): boolean {
	return (file.mimeType?.startsWith('image/') ?? false) || PACKET_IMAGE_EXTENSIONS.has((file.fileType ?? '').toLowerCase());
}

/** Human label for an attachment's checklist slot, if the checklist defines it. */
export function checklistLabelFor(itemKey: string | null | undefined, checklist: ChecklistItemRef[]): string | null {
	if (!itemKey) return null;
	return checklist.find((item) => item.key === itemKey)?.label ?? null;
}

/** One attachment as the printed packet needs it: ordered, and captioned by its slot. */
export interface PacketEntry<T> {
	attachment: T;
	/** The checklist slot's human label, or null for an untagged upload. */
	caption: string | null;
	/**
	 * The slot's KEY, which is what "is this requirement satisfied?" must be answered against.
	 * Labels are human text and two requirements may legitimately share one; the key is the
	 * identity.
	 */
	slot: string | null;
}

export interface AssembledPacket<T> {
	/** Printable image pages, in checklist order. One per sheet. */
	pages: Array<PacketEntry<T>>;
	/** Everything that cannot be laid out as a page — PDFs and other files — same order. */
	documents: Array<PacketEntry<T>>;
	/** What the request type ASKED for, so the packet states the gaps as well as the files. */
	requirements: ChecklistItemRef[];
}

/**
 * Assemble an application's attachments into a board packet.
 *
 * The whole packet in ONE pure function, deliberately: the print page is a React component
 * whose render path is expensive to exercise, and the ordering is the part that carries the
 * requirement. Keeping it here means the rule is testable without a renderer — which is how
 * it came to be orphaned in the first place, when a rewrite of the page silently stopped
 * calling these helpers and nothing failed.
 *
 * `checklistKey` is the current name for what this module has always called `itemKey`; the
 * accessor bridges them rather than forcing either side to rename.
 */
export function assemblePacket<T extends { mimeType?: string | null; createdAt?: string }>(
	attachments: T[],
	checklist: ChecklistItemRef[],
	slotOf: (attachment: T) => string | null | undefined,
	/**
	 * Is this file printable as a packet PAGE?
	 *
	 * Overridable because MIME alone is not trustworthy here. Two upload paths store photos as
	 * `application/octet-stream` — a presigned-host form upload that reports no content-type
	 * (`api/message-uploads.ts`) and a client that omits `mimeType`
	 * (`api/exterior-applications.ts`). Classifying on MIME only would demote those photos to a
	 * one-line bullet under "Documents", silently dropping them from the packet the board
	 * actually reads. Callers that know the filename extension should say so.
	 */
	isPage?: (attachment: T) => boolean,
): AssembledPacket<T> {
	const ordered = orderAttachmentsByChecklist(
		attachments.map((attachment) => ({
			attachment,
			itemKey: slotOf(attachment) ?? null,
			createdAt: attachment.createdAt ?? '',
		})),
		checklist,
	);
	const entries = ordered.map((row) => ({
		attachment: row.attachment,
		caption: checklistLabelFor(row.itemKey, checklist),
		// Null unless the slot is one THIS checklist defines — a stale key from a request
		// type that has since changed must not tick a requirement that no longer exists.
		slot: checklist.some((item) => item.key === row.itemKey) ? row.itemKey : null,
		mimeType: row.attachment.mimeType ?? undefined,
	}));

	if (!isPage) {
		// Carries `mimeType` so the existing splitter does the sorting it already knows how to
		// do, rather than a second copy of the image/PDF rule living here.
		const { images, pdfs, other } = splitPacketAttachments(entries);
		return { pages: images, documents: [...pdfs, ...other], requirements: checklist };
	}

	const pages: typeof entries = [];
	const documents: typeof entries = [];
	for (const entry of entries) {
		(isPage(entry.attachment) ? pages : documents).push(entry);
	}
	return { pages, documents, requirements: checklist };
}
